import { Modal, Spin, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import React from 'react'
import styled from 'styled-components/macro'
import { buildPriceAuditTableRows } from 'src/utils/priceAuditTableRows'

const formatCataloguePrice = price => {
  if (price == null || price === '') return '—'
  const n = Number(price)
  if (!Number.isFinite(n)) return '—'
  return `£${n.toFixed(2)}`
}

const getSupplierLabel = product => {
  const title = product?.supplier?.title
  const secondTitle = product?.supplier?.secondTitle
  if (title && secondTitle) return `${title} (${secondTitle})`
  if (title) return title
  return '—'
}

const ProductPriceAuditModal = ({
  visible,
  onClose,
  product,
  priceHistory = [],
  loading = false,
  error = null,
}) => {
  const tableRows = buildPriceAuditTableRows(priceHistory)
  const hasLegacyRows = tableRows.some(row => row.isLegacy)

  return (
    <Modal
      title='Price audit'
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
    >
      <Header>
        <HeaderItem>
          <Label>Product</Label>
          <Value>{product?.title || '—'}</Value>
        </HeaderItem>
        <HeaderItem>
          <Label>SKU</Label>
          <Value>{product?.sku || '—'}</Value>
        </HeaderItem>
        <HeaderItem>
          <Label>Supplier</Label>
          <Value>{getSupplierLabel(product)}</Value>
        </HeaderItem>
        <HeaderItem>
          <Label>Current price</Label>
          <Value>{formatCataloguePrice(product?.price)}</Value>
        </HeaderItem>
      </Header>

      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      {hasLegacyRows ? (
        <LegacyNotice>
          Older entries may only show a previous price and limited audit details
          (who changed it and source were not recorded).
        </LegacyNotice>
      ) : null}

      <Spin spinning={loading}>
        <Table
          dataSource={tableRows}
          pagination={false}
          scroll={{ x: true }}
          locale={{
            emptyText: loading
              ? 'Loading price history…'
              : 'No price changes recorded for this product.',
          }}
          columns={[
            {
              title: 'When',
              dataIndex: 'updatedAt',
              key: 'updatedAt',
              className: 'restrictShrink',
              render: value =>
                value ? dayjs(value).format('D MMM YYYY, HH:mm') : '—',
            },
            {
              title: 'From',
              dataIndex: 'fromPrice',
              key: 'fromPrice',
              className: 'restrictShrink',
              render: (text, row) => (
                <LegacyCell $muted={row.isLegacyPrice}>
                  {text}
                  {row.isLegacyPrice ? (
                    <Tag style={{ marginLeft: 8 }}>Legacy</Tag>
                  ) : null}
                </LegacyCell>
              ),
            },
            {
              title: 'To',
              dataIndex: 'toPrice',
              key: 'toPrice',
              className: 'restrictShrink',
              render: (text, row) => (
                <LegacyCell $muted={row.isLegacyPrice}>{text}</LegacyCell>
              ),
            },
            {
              title: 'Changed by',
              dataIndex: 'changedBy',
              key: 'changedBy',
              className: 'restrictShrink',
              render: (text, row) => (
                <LegacyCell $muted={row.isLegacyAudit}>{text}</LegacyCell>
              ),
            },
            {
              title: 'Source',
              dataIndex: 'source',
              key: 'source',
              className: 'restrictShrink',
              render: (text, row) => (
                <LegacyCell $muted={row.isLegacyAudit}>{text}</LegacyCell>
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  )
}

const Header = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colours.bgGrey};
`

const HeaderItem = styled.div`
  min-width: 0;
`

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.textGrey || '#888'};
  margin-bottom: 4px;
`

const Value = styled.div`
  font-weight: 600;
  word-break: break-word;
`

const ErrorMessage = styled.p`
  color: #cf1322;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const LegacyNotice = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colours.bgGrey};
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.textGrey || '#666'};
`

const LegacyCell = styled.span`
  color: ${({ $muted, theme }) =>
    $muted ? theme.colours.textGrey || '#888' : 'inherit'};
`

export default ProductPriceAuditModal
